const _excluded = ["levels", "max", "min", "showTicks", "showRange", "trackWidth"];
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
import "core-js/modules/es.array.map.js";
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useMemo, useEffect } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useEuiTheme } from '../../../services';
import { logicalStyles } from '../../../global_styling';
import { isNamedLevelColor } from './range_levels_colors';
import { euiRangeLevelsStyles, euiRangeLevelStyles } from './range_levels.styles';
import { calculateThumbPosition, EUI_THUMB_SIZE } from './utils';
export var EuiRangeLevels = function EuiRangeLevels(_ref) {
  var _ref$levels = _ref.levels,
    levels = _ref$levels === void 0 ? [] : _ref$levels,
    max = _ref.max,
    min = _ref.min,
    showTicks = _ref.showTicks,
    showRange = _ref.showRange,
    trackWidth = _ref.trackWidth,
    rest = _objectWithoutProperties(_ref, _excluded);
  var euiTheme = useEuiTheme();
  var styles = euiRangeLevelsStyles(euiTheme);
  var cssStyles = [styles.euiRangeLevels, showTicks && styles.hasTicks, showRange && styles.hasRange];
  return ___EmotionJSX("div", _extends({
    className: "euiRangeLevels",
    css: cssStyles
  }, rest), levels.map(function (level, index) {
    return ___EmotionJSX(EuiRangeLevelElement, {
      key: index,
      level: level,
      min: min,
      max: max,
      trackWidth: trackWidth
    });
  }));
};

// Internal subcomponent
EuiRangeLevels.propTypes = {
  trackWidth: PropTypes.number.isRequired,
  style: PropTypes.any
};
var EuiRangeLevelElement = ({
  level,
  min,
  max,
  trackWidth
}) => {
  const {
    color,
    className,
    min: levelMin,
    max: levelMax,
    ...levelRest
  } = level;
  const isNamedColor = useMemo(() => isNamedLevelColor(color), [color]);
  useEffect(() => {
    validateLevelIsInRange({
      min: levelMin,
      max: levelMax
    }, {
      min,
      max
    });
  }, [levelMin, levelMax, min, max]);
  const styles = useMemo(() => {
    let left = 0;
    let right = 0;
    let leftOffset = 0;
    let rightOffset = 0;
    if (trackWidth > 0) {
      left = levelMin === min ? 0 : calculateThumbPosition(levelMin, min, max, trackWidth);
      leftOffset = calculateOffset(left, levelMin, min);
      right = levelMax === max ? 100 : calculateThumbPosition(levelMax, min, max, trackWidth);
      rightOffset = calculateOffset(right, levelMax, max);
    }
    return logicalStyles({
      left: `calc(${left}% + ${leftOffset}px)`,
      right: `calc(${100 - right}% - ${rightOffset}px)`,
      backgroundColor: !isNamedColor ? color : undefined
    });
  }, [levelMin, levelMax, min, max, trackWidth, isNamedColor, color]);
  const levelClasses = classNames('euiRangeLevel', className);
  const euiTheme = useEuiTheme();
  const levelStyles = euiRangeLevelStyles(euiTheme);
  const cssLevelStyles = [levelStyles.euiRangeLevel, isNamedColor ? levelStyles[color] : levelStyles.customColor];
  return <span style={styles} className={levelClasses} css={cssLevelStyles} {...levelRest} />;
};
EuiRangeLevelElement.propTypes = {
  level: PropTypes.shape({
    /**
       * Accepts one of `["primary", "success", "warning", "danger"]` or a valid CSS color value.
       */
    color: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired,
  min: PropTypes.any.isRequired,
  max: PropTypes.any.isRequired,
  trackWidth: PropTypes.number.isRequired
};
const validateLevelIsInRange = (level, {
  min,
  max
}) => {
  if (level.min < min) {
    throw new Error(`The level min of ${level.min} is lower than the min value of ${min}.`);
  }
  if (level.max > max) {
    throw new Error(`The level max of ${level.max} is higher than the max value of ${max}.`);
  }
};
const calculateOffset = (position, value, bound) => {
  const threshold = 30;
  let offset = value === bound ? 0 : EUI_THUMB_SIZE / 2;
  if (offset !== 0) {
    // Estimating offset by eye. Trying to account for range scaling at both ends.
    offset = position <= threshold ? offset + 1 / position * threshold : offset;
    offset = position >= 100 - threshold ? offset - 1 / (100 - position) * threshold : offset;
  }
  return offset;
};
try {
  EuiRangeLevels.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/range/range_levels.tsx',
    description: '',
    displayName: 'EuiRangeLevels',
    methods: [],
    props: {
      trackWidth: {
        defaultValue: null,
        description: '',
        name: 'trackWidth',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      levels: {
        defaultValue: {
          value: '[]'
        },
        description: 'Create colored indicators for certain intervals.\n' + 'An array of #EuiRangeLevel objects',
        name: 'levels',
        parent: {
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeDataStructures'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeDataStructures'
        }],
        required: false,
        type: {
          name: 'EuiRangeLevel[]'
        }
      },
      max: {
        defaultValue: null,
        description: '',
        name: 'max',
        parent: {
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeValuesProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeValuesProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      min: {
        defaultValue: null,
        description: '',
        name: 'min',
        parent: {
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeValuesProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeValuesProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      showRange: {
        defaultValue: null,
        description: 'Shows a thick line from min to value',
        name: 'showRange',
        parent: {
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeVisualConfiguration'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeVisualConfiguration'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTicks: {
        defaultValue: null,
        description: 'Shows clickable tick marks and labels at the given interval (`step`/`tickInterval`)',
        name: 'showTicks',
        parent: {
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeVisualConfiguration'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/types.ts',
          name: '_SharedRangeVisualConfiguration'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiRangeLevelsProps', '_SharedRangeDataStructures', '_SharedRangeValuesProps', '_SharedRangeVisualConfiguration']
  };
} catch (e) {}